import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/ToastContext';
import {
  closeGuestSupportTicket,
  createGuestSupportTicket,
  fetchGuestSupportTicket,
  fetchGuestSupportTickets,
} from '../../api/guestApi';

const categories = [
  { value: 'BOOKINGS', label: 'Bookings' },
  { value: 'PAYMENTS', label: 'Payments' },
  { value: 'CANCELLATIONS', label: 'Cancellations' },
  { value: 'CHECK_IN', label: 'Check-in' },
  { value: 'PROPERTY', label: 'Property' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'OTHER', label: 'Other' },
];

const statusStyles = {
  open: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-100',
  resolved: 'bg-sky-50 text-sky-700 border-sky-100',
  closed: 'bg-slate-100 text-slate-600 border-slate-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  default: 'bg-slate-100 text-slate-600 border-slate-200',
};

const toTitle = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const StatusChip = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = statusStyles[key] || statusStyles.default;
  return <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${cls}`}>{toTitle(value)}</span>;
};

const isClosed = (ticket) => String(ticket?.status || '').toUpperCase() === 'CLOSED';

const GuestSupport = () => {
  const toast = useToast();
  const [form, setForm] = useState({ subject: '', category: 'BOOKINGS', message: '' });
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 8 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actionId, setActionId] = useState(null);

  const loadTickets = async (nextPage = page) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchGuestSupportTickets({ page: nextPage, limit: pagination.limit || 8 });
      setTickets(res.data || []);
      setPagination(res.pagination || { page: nextPage, totalPages: 1, total: 0, limit: pagination.limit || 8 });
    } catch (err) {
      setError(err.message || 'Unable to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const pageSummary = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total || 0);
    if (!pagination.total) return '0 tickets';
    return `${start}-${end} of ${pagination.total}`;
  }, [pagination]);

  const openDetail = async (ticketId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelected(null);
    try {
      const ticket = await fetchGuestSupportTicket(ticketId);
      setSelected(ticket);
    } catch (err) {
      toast.error(err.message || 'Unable to load ticket');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelected(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const subject = String(form.subject || '').trim();
    const message = String(form.message || '').trim();
    if (!subject) {
      toast.error('Subject is required');
      return;
    }
    if (!message) {
      toast.error('Message is required');
      return;
    }
    setSubmitting(true);
    try {
      await createGuestSupportTicket({
        subject,
        category: form.category,
        message,
      });
      setForm({ subject: '', category: form.category, message: '' });
      toast.success('Ticket created');
      setPage(1);
      await loadTickets(1);
    } catch (err) {
      toast.error(err.message || 'Unable to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async (ticket) => {
    if (!ticket || isClosed(ticket)) return;
    const ok = window.confirm('Close this ticket?');
    if (!ok) return;
    setActionId(ticket.id);
    try {
      const updated = await closeGuestSupportTicket(ticket.id);
      toast.success('Ticket closed');
      await loadTickets(page);
      if (selected?.id === ticket.id) {
        setSelected(updated);
      }
    } catch (err) {
      toast.error(err.message || 'Unable to close ticket');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-600">Create ticket</p>
            <p className="text-lg font-semibold text-slate-900">Need help? Let us know.</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="Issue with my booking"
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            >
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">Message</label>
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Describe the issue and any booking details."
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting ? 'Submitting...' : 'Create ticket'}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-slate-900">Your tickets</p>
            <p className="text-xs text-slate-500">Track your support requests and status updates.</p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{pageSummary}</span>
        </div>

        {error && <div className="px-5 py-3 text-sm text-rose-600">{error}</div>}
        {loading && <div className="px-5 py-3 text-sm text-slate-500">Loading tickets...</div>}

        {!loading && !error && tickets.length === 0 && (
          <div className="px-5 py-6 text-sm text-slate-500">No tickets yet. Create one above.</div>
        )}

        {!loading && !error && tickets.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <tr>
                  <th className="px-5 py-3">Ticket</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Updated</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tickets.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-900">{t.subject}</div>
                      <div className="text-xs text-slate-400">#{t.id}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{toTitle(t.category)}</td>
                    <td className="px-5 py-3">
                      <StatusChip value={t.status} />
                    </td>
                    <td className="px-5 py-3 text-slate-600">{formatDateTime(t.updated_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => openDetail(t.id)}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-200 hover:text-emerald-700"
                        >
                          View
                        </button>
                        {!isClosed(t) && (
                          <button
                            type="button"
                            onClick={() => handleClose(t)}
                            disabled={actionId === t.id}
                            className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 disabled:opacity-50"
                          >
                            Close
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4 text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Previous
          </button>
          <div className="text-slate-600">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
            disabled={page >= pagination.totalPages}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            onClick={closeDetail}
            className="absolute inset-0 bg-slate-950/60"
            aria-label="Close ticket detail"
          />
          <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Support ticket</p>
                <p className="text-lg font-semibold text-slate-900">{selected?.subject || 'Ticket'}</p>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600"
              >
                Close
              </button>
            </div>

            {detailLoading && <div className="px-5 py-6 text-sm text-slate-500">Loading ticket...</div>}

            {!detailLoading && selected && (
              <div className="grid gap-5 px-5 py-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Status</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <StatusChip value={selected.status} />
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                        {toTitle(selected.category)}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Message</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{selected.message || '-'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Ticket info</p>
                    <div className="mt-3 space-y-2 text-sm text-slate-600">
                      <div>
                        <span className="text-xs text-slate-400">Ticket ID</span>
                        <div className="font-semibold text-slate-900">#{selected.id}</div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400">Updated</span>
                        <div className="font-semibold text-slate-900">{formatDateTime(selected.updated_at)}</div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400">Assigned</span>
                        <div className="font-semibold text-slate-900">
                          {selected.assigned_to ? `Admin #${selected.assigned_to}` : 'Unassigned'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {!isClosed(selected) && (
                    <button
                      type="button"
                      onClick={() => handleClose(selected)}
                      disabled={actionId === selected.id}
                      className="w-full rounded-lg bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 disabled:opacity-50"
                    >
                      Close ticket
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestSupport;
